import { Card } from '@/components/ui/Card'
import useSetupStore from '@/store/useSetupStore'
import TeamType from './steps/TeamType'
import { Button, ButtonText } from '@/components/ui/button'
import {
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
} from '@heroicons/react/16/solid'
import AboutYourBusiness from './steps/business/AboutYourBusiness'
import CreateVault from './steps/business/CreateVault'
import { useForm } from 'react-hook-form'
import useCreateTeam from '@/services/api/team/useCreateTeam.js'
import { useEffect } from 'react'
import useAuthStore from '@/store/useAuthStore'
import { useNavigate } from 'react-router-dom'

export default function SetupPage() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const teamType = useSetupStore((state) => state.teamType)
  const steps = useSetupStore((state) => state[teamType]?.steps)
  const currentStep = useSetupStore((state) => state.currentStep)
  const moveNextStep = useSetupStore((state) => state.moveNextStep)
  const movePreviousStep = useSetupStore((state) => state.movePreviousStep)
  const setTeamTypeSettings = useSetupStore(
    (state) => state.setTeamTypeSettings,
  )
  const isLastStep = useSetupStore((state) => state.isLastPage)()

  const getFormData = useSetupStore((state) => state.getData)
  const form = useForm()
  const navigate = useNavigate()

  const { createTeam, createTeamStatus } = useCreateTeam({
    setError: form.setError,
    clientServerFieldMapping: {
      teamType: 'team_type',
      name: 'name',
    },
  })

  useEffect(() => {
    if (!isAuthenticated) navigate('/login')
  }, [navigate, isAuthenticated])

  const title =
    currentStep === null
      ? 'What kind of finances do you want to manage?'
      : currentStep.title
  const description =
    currentStep === null
      ? 'Thank you for signing in for ProfitFlow. This setup wizard will help you to navigate through the setup process'
      : currentStep.description

  const componentMapping = {
    AboutYourBusiness: AboutYourBusiness,
    CreateVault: CreateVault,
  }
  const Component = componentMapping[currentStep?.component]

  async function moveNext(vals) {
    setTeamTypeSettings(vals)
    if (!isLastStep) return moveNextStep()

    const data = getFormData()

    await createTeam({ ...data })
    window.location.href = '/'
  }

  const { errors } = form.formState

  useEffect(() => {
    return
  }, [errors])
  return (
    <form
      //   onSubmit={form.handleSubmit(moveNext)}
      onSubmit={(e) => {
        e.preventDefault()
        // createTeamStatus.reset()
        form.clearErrors()
        form.handleSubmit(moveNext)()
      }}
    >
      <div className="grid min-h-screen">
        <Card className="my-12 flex min-h-[520px] w-[904px] flex-col place-self-center">
          <div className="grid h-full flex-1 grid-cols-[324px,1fr]">
            <div className="flex items-end bg-accent-tr-08 p-8">
              <div className="">
                <h1 className="font-bold text-txt-accent">{title}</h1>
                <p className="mt-2">{description}</p>
              </div>
            </div>
            <div className="flex flex-col justify-between p-8 pb-0">
              <div>
                {(teamType === null || currentStep === null) && <TeamType />}
                {!!Component && <Component form={form} />}
              </div>
              <footer className="relative flex items-center justify-between pb-8 pt-6">
                <div>
                  {currentStep !== null && (
                    <Button
                      variant="outline"
                      size="lg"
                      disabled={teamType === null}
                      onClick={() => movePreviousStep()}
                      type="button"
                    >
                      <ArrowLongLeftIcon className="size-5" />
                      <ButtonText>Previous</ButtonText>
                    </Button>
                  )}
                </div>
                <div className="absolute left-2/4 top-2/4 flex -translate-x-2/4 -translate-y-2/4 gap-2">
                  <div
                    data-active={currentStep === null}
                    className="size-3 rounded-full border border-primary bg-gray-tr-08 data-[active=true]:bg-black"
                  ></div>

                  {(steps ?? [{ id: 'x' }, { id: 'y' }]).map((step) => (
                    <div
                      key={step.id}
                      data-active={step.id === currentStep?.id}
                      className="size-3 rounded-full border border-primary bg-gray-tr-08 data-[active=true]:bg-black"
                    ></div>
                  ))}
                </div>
                <div>
                  <Button
                    size="lg"
                    type="submit"
                    disabled={teamType === null}
                    loading={createTeamStatus.status === 'pending'}
                  >
                    <ButtonText>
                      {isLastStep ? 'Finish' : 'Next step'}
                    </ButtonText>
                    {!isLastStep && <ArrowLongRightIcon className="size-5" />}
                  </Button>
                </div>
              </footer>
            </div>
          </div>
        </Card>
      </div>
    </form>
  )
}
